import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import Button from '@mui/material/Button';
import { Dialog, DialogTitle } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { format, addMonths, subMonths } from 'date-fns';
import { defaultMuiProps } from '../config';
import { useLocale } from 'hooks';
import { TextField } from './../';
import { Paper, ButtonsContainer, datePickerTheme } from './style';

// Custom header with left/right arrows for day view
function CustomCalendarHeader({ currentMonth, onMonthChange }) {
  const monthLabel = format(currentMonth, 'MMMM yyyy');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 5px',
        marginTop: '4px',
        marginBottom: '8px'
      }}
    >
      <button
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          rotate: '180deg'
        }}
        onClick={() => onMonthChange(subMonths(currentMonth, 1))}
      >
        <svg
          viewBox="0 0 24 24"
          style={{ fill: 'rgba(0, 0, 0, 0.54)', width: '1em', height: '1em', fontSize: '1.5rem' }}
        >
          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"></path>
          <path fill="none" d="M0 0h24v24H0V0z"></path>
        </svg>
      </button>
      <div style={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>{monthLabel}</div>
      <button
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => onMonthChange(addMonths(currentMonth, 1))}
      >
        <svg
          viewBox="0 0 24 24"
          style={{ fill: 'rgba(0, 0, 0, 0.54)', width: '1em', height: '1em', fontSize: '1.5rem' }}
        >
          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"></path>
          <path fill="none" d="M0 0h24v24H0V0z"></path>
        </svg>
      </button>
    </div>
  );
}

function CustomDateField({
  cancelLabel = defaultMuiProps.cancelLabel,
  className,
  disableFuture = false,
  doneLabel = defaultMuiProps.doneLabel,
  label,
  selectDateLabel,
  selectMonthLabel,
  value,
  onChange,
  autoOk = false
}) {
  const locale = useLocale();
  const paperRef = useRef(null);

  const parseValue = useCallback(val => {
    if (!val) return new Date();
    const parts = val.split('/');
    let day = null,
      month = null,
      year = null;
    if (parts.length === 3) [day, month, year] = parts.map(Number);
    else if (parts.length === 2) [month, year] = parts.map(Number);
    else [year] = parts.map(Number);
    return new Date(
      year ?? new Date().getFullYear(),
      (month ?? new Date().getMonth() + 1) - 1,
      day ?? new Date().getDate()
    );
  }, []);

  const [currentDate, setCurrentDate] = useState(parseValue(value));
  const [isOpen, setOpen] = useState(false);
  const [currentView, setCurrentView] = useState('year');

  useEffect(() => setCurrentDate(parseValue(value)), [value, parseValue]);
  useEffect(() => {
    if (!isOpen || currentView !== 'year') return;
    const frame = requestAnimationFrame(() => {
      const yearList = paperRef.current?.querySelector('.MuiYearCalendar-root');
      if (!yearList) return;
      const selectedYear = String(currentDate.getFullYear());
      const yearButtons = yearList.querySelectorAll('button');
      const target = Array.from(yearButtons).find(button => button.textContent === selectedYear);
      if (target) target.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, currentView, currentDate]);

  const handleMonthChange = useCallback(newMonth => setCurrentDate(newMonth), []);
  const handlePickerChange = useCallback(date => setCurrentDate(date), []);

  const handleDone = useCallback(() => {
    let fmt;
    switch (currentView) {
      case 'year':
        fmt = 'yyyy';
        break;
      case 'month':
        fmt = 'MM/yyyy';
        break;
      case 'date':
        fmt = 'dd/MM/yyyy';
        break;
      default:
        fmt = null;
    }
    if (fmt) onChange(format(currentDate, fmt));
    setOpen(false);
  }, [currentDate, currentView, onChange]);

  return (
    <>
      <TextField
        className={className}
        label={label}
        readOnly
        margin="normal"
        value={value}
        onClick={() => {
          setOpen(true);
          setCurrentView('year');
        }}
      />
      <Dialog
        PaperProps={{ component: Paper, ref: paperRef }}
        open={isOpen}
        onClose={() => setOpen(false)}
        maxWidth={false}
      >
        <DialogTitle>{label}</DialogTitle>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <ThemeProvider theme={datePickerTheme}>
            {currentView === 'year' && (
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                openTo="year"
                views={['year']}
                value={currentDate}
                onChange={handlePickerChange}
                disableFuture={disableFuture}
                slots={{ textField: () => <div /> }}
              />
            )}
            {currentView === 'month' && (
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                openTo="month"
                views={['year', 'month']}
                value={currentDate}
                onChange={handlePickerChange}
                disableFuture={disableFuture}
                slots={{ textField: () => <div /> }}
              />
            )}
            {currentView === 'date' && (
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                openTo="day"
                views={['day']}
                value={currentDate}
                onChange={handlePickerChange}
                disableFuture={disableFuture}
                dayOfWeekFormatter={(day, date) => {
                  return format(date, 'EEEEEE', { locale });
                }}
                slots={{
                  calendarHeader: props => (
                    <CustomCalendarHeader {...props} onMonthChange={handleMonthChange} />
                  ),
                  textField: () => <div />
                }}
              />
            )}
          </ThemeProvider>
        </LocalizationProvider>

        <ButtonsContainer>
          <Button onClick={() => setOpen(false)}>{cancelLabel}</Button>
          {currentView === 'year' && (
            <Button color="primary" onClick={() => setCurrentView('month')}>
              {selectMonthLabel}
            </Button>
          )}
          {currentView === 'month' && (
            <Button color="primary" onClick={() => setCurrentView('date')}>
              {selectDateLabel}
            </Button>
          )}
          <Button color="secondary" onClick={handleDone}>
            {doneLabel}
          </Button>
        </ButtonsContainer>
      </Dialog>
    </>
  );
}

CustomDateField.propTypes = {
  cancelLabel: PropTypes.string,
  className: PropTypes.string,
  disableFuture: PropTypes.bool.isRequired,
  doneLabel: PropTypes.string,
  label: PropTypes.string,
  selectDateLabel: PropTypes.string.isRequired,
  selectMonthLabel: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default React.memo(CustomDateField);
